import * as React from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  Link as MUILink,
  TextField,
} from "@material-ui/core";
import { tl } from "../../utils";
import * as styles from "./styles.scss";
import { IAPIData, IAPIMessage, IDoubleauthData } from "./types";
import { MUIDialog } from "../panel/MUI";
import { SetPhone } from "./SetPhone";

export const PhoneConfirm = ({
  doubleauthData,
  apiData,
  isCourse,
  host,
}: {
  apiData: IAPIData;
  doubleauthData: IDoubleauthData | null;
  isCourse: boolean;
  host: string;
}) => {
  const history = useHistory();

  const [message, setMessage] = React.useState<IAPIMessage>(null);
  const [disabled, setDisabled] = React.useState<boolean>(false);
  const [code, setCode] = React.useState<string>("");
  const [phone, setPhone] = React.useState<string>(
    !doubleauthData.phone || doubleauthData.phone === "****"
      ? ""
      : doubleauthData.phone
  );
  const [timer, setTimer] = React.useState<number>(null);
  const toRef = React.useRef<ReturnType<typeof setTimeout>>(null);

  const onPhoneConfirm = React.useCallback(async () => {
    const data = new FormData();
    data.append("service", "at");
    if (doubleauthData.login && doubleauthData.password) {
      data.append("login", doubleauthData.login);
      data.append("password", doubleauthData.password);
      data.append("code", code);
    } else {
      data.append(
        (doubleauthData.doubleauth === "sms" || doubleauthData.doubleauth === "call") ? "code" : "google_code",
        code
      );
    }

    const url =
      doubleauthData.login && doubleauthData.password
        ? `${apiData.url}/api/account/login`
        : `/login/doubleauth`;

    const response = await fetch(url, {
      method: "post",
      credentials: "include",
      body: data,
      headers: host ? {
        'X-App-Host': host,
      } : {},
    });
    switch (response.status) {
      case 403:
        setMessage({
          message: 
            `${tl('Неверный код')}. ${
              doubleauthData.doubleauth === "sms"
                ? tl("Попробуйте снова или перезагрузите страницу чтобы получить новый код.")
                : doubleauthData.doubleauth === "call" ? tl(" Попробуйте снова или перезагрузите страницу, чтобы получить новй звонок.")
                : tl("Попробуйте снова.") 
            }`
          ,
          messageType: "error",
        });
        setDisabled(false);
        setCode("");
      case 200:
        const result = await response.json();
        if (result.result === 'ok') {       
          if (doubleauthData.login && doubleauthData.password) {
            const redirectUrl = new URL(result.redirectLink);
            window.location.href = `${redirectUrl.pathname}?token=${result.token}&redirect_uri=${apiData.redirect}`;
          } else {
            ((window as any).location.href = doubleauthData.url);
          }
        } else {
          setMessage({
            message: 
              `${tl('Неверный код')}. ${
                doubleauthData.doubleauth === "sms"
                  ? tl("Попробуйте снова или перезагрузите страницу чтобы получить новый код.")
                  : doubleauthData.doubleauth === "call" ? tl(" Попробуйте снова или перезагрузите страницу, чтобы получить новй звонок.")
                  : tl("Попробуйте снова.") 
              }`
            ,
            messageType: "error",
          });
          setDisabled(false);
          setCode("");
        }
    }
  }, [code]);

  const onTOTick = React.useCallback((value?: number) => {
    setTimer((prevTimer) => {
      if (prevTimer !== 0) {
        toRef.current = setTimeout(onTOTick, 1000);
      }
      return value
        ? value
        : prevTimer === 0
        ? null
        : !prevTimer
        ? 60
        : prevTimer - 1;
    });
  }, []);

  const getCode = React.useCallback(async (value: any) => {
    if (!value || typeof value !== "number") {
      setCode("");
      setDisabled(false);

      const data = new FormData();
      data.append("service", "at");
      data.append("resend", "true");

      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.has("course_id")) {
        data.append("course_id", queryParams.get("course_id"));
      }

      if (apiData.redirect) {
        data.append("redirect_uri", apiData.redirect);
      }

      if (doubleauthData.login && doubleauthData.password) {
        data.append("login", doubleauthData.login);
        data.append("password", doubleauthData.password);
      }

      if (doubleauthData.login && doubleauthData.password) {
        const response = await fetch(`${apiData.url}/api/account/login`, {
          method: "post",
          credentials: "include",
          body: data,
        });
        const result = await response.json();
      } else {
        const response = await fetch(`/login/doubleauth`, {
          method: "post",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "include",
          body: data,
        });
        const result = await response.json();
      }
    }
    onTOTick(typeof value === "number" ? value : null);
  }, []);

  const onSignOut = async () => {
    await fetch(`/account/logout`);

    history.push("/login");
  };

  React.useEffect(() => {
    getCode(doubleauthData.timeout);
  }, []);

  React.useEffect(() => {
    if (
      ((doubleauthData.doubleauth === "sms" || doubleauthData.doubleauth === "call") && code.length === 5) ||
      (doubleauthData.doubleauth === "google" && code.length === 6)
    ) {
      setDisabled(true);
      onPhoneConfirm();
    }
  }, [code]);

  return (doubleauthData.doubleauth === "sms" || doubleauthData.doubleauth === "call") && !phone ? (
    <SetPhone setPhoneOuter={setPhone} isCourse={isCourse} />
  ) : (
    <div className={`${styles.wrap} ${styles.wrapRestore} ${styles.taCenter}`}>
      <Grid container spacing={2} direction="column">
        <Grid item>
          <Box className={styles.title} textAlign="center">
            {isCourse
              ? tl("Защита доступа к курсу")
              : tl("Защита доступа к аккаунту")}
          </Box>
        </Grid>
        {doubleauthData.doubleauth === "sms" ? (
          <Grid item>
            {tl("Мы выслали код на Ваш телефон")} {doubleauthData.phone}
          </Grid>
        ) : null}
        {doubleauthData.doubleauth === "call" ? (
          <Grid item>
            {tl("Мы позвонили на Ваш телефон")} {doubleauthData.phone}
          </Grid>
        ) : null}
        <Grid item>
          <TextField
            variant="outlined"
            color="primary"
            fullWidth
            placeholder={doubleauthData.doubleauth === "call" ? 
            tl("Прослушайте сообщение и введите код") : 
            
            `${tl('Введите сюда код из')} ${
                doubleauthData.doubleauth === "sms"
                  ? tl("СМС")
                  : tl("приложения Google Authenticator")
              }`
            }
            value={code}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            onChange={(evt) => {
              setCode(evt.target.value);
            }}
            disabled={disabled}
          />
        </Grid>
        {doubleauthData.doubleauth === "sms" ? (
          <Grid item>
            {timer > 0 || timer === 0 ? (
              <>
                {tl("Повторно выслать код можно будет через: ")}
                {timer}
                {tl(" сек.")}
              </>
            ) : (
              <Button variant="contained" fullWidth onClick={getCode}>
                {tl("Выслать код повторно")}
              </Button>
            )}
          </Grid>
        ) : null}
        <Grid item>
          {isCourse ? (
            <Box mx={-2}>
              {
                `${tl('Если у вас нет доступа к')} ${
                  (doubleauthData.doubleauth === "sms" || doubleauthData.doubleauth === "call")
                    ? tl("телефону")
                    : tl("приложению")
                }`
              }
              &nbsp;&mdash; {tl("обратитесь к")} <b>{tl("автору курса")}</b>
            </Box>
          ) : (
            <Box mx={-2}>
              {tl(
                `Если у Вас возникли сложности с авторизацией, пожалуйста, обратитесь в техническую поддержку`
              )}
            </Box>
          )}
        </Grid>
        <Grid item>
          <MUILink onClick={onSignOut}>{tl("Сменить аккаунт")}</MUILink>
        </Grid>
      </Grid>
      {message ? (
        <MUIDialog
          open={!!message}
          onClose={() => {
            setMessage(null);
          }}
          maxWidth="xs"
          title={message.messageType === "error" ? tl("Ошибка!") : tl("Внимание!")}
          actions={[
            [],
            [
              {
                label: tl("Понятно"),
                btnProps: {
                  onClick: () => {
                    setMessage(null);
                  },
                },
              },
            ],
          ]}
        >
          {message.message}
        </MUIDialog>
      ) : null}
    </div>
  );
};
